import { NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, User, Settings, LifeBuoy, PanelLeftClose, PanelLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { TintiLogo, TintiMark } from "./TintiLogo";
import { NAV_GROUPS } from "./nav-config";
import { UserAvatar } from "@/components/common/Avatars";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { demoAction } from "@/lib/demo";
import { useAuth } from "@/hooks/useAuth";

const initialsOf = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";

const NavItem = ({ item, collapsed, onNavigate }) => {
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={`nav-${item.label}`}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          collapsed && "justify-center px-0",
          isActive
            ? "bg-brand/10 text-foreground"
            : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand" />}
          <item.icon className={cn("h-[18px] w-[18px] shrink-0", isActive && "text-brand")} />
          {!collapsed && <span className="truncate">{item.label}</span>}
        </>
      )}
    </NavLink>
  );

  if (!collapsed) return link;
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" className="border-line bg-popover">{item.label}</TooltipContent>
    </Tooltip>
  );
};

export const Sidebar = ({ collapsed, onToggle, onNavigate, mobile = false }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const displayName = user?.name || "—";
  const displayRole = user?.roles?.[0] || "—";
  const displayEmail = user?.email || "";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <TooltipProvider>
      <aside
        className={cn(
          "relative flex h-full flex-col border-r border-line bg-sidebar",
          collapsed ? "w-16" : "w-64",
        )}
        data-testid="sidebar"
      >
        {/* Header */}
        {/* Logo oficial usa toda a largura útil; o recolher fica numa aba na borda. */}
        <div className={cn("flex h-[4.5rem] shrink-0 items-center border-b border-line px-3", collapsed && "h-16 justify-center px-0")}>
          {collapsed ? <TintiMark className="h-8 w-8" /> : <TintiLogo className="mx-auto h-auto w-[74%] max-w-[174px]" />}
        </div>
        {!mobile && !collapsed && (
          <button
            onClick={onToggle}
            data-testid="sidebar-collapse-btn"
            aria-label="Recolher menu"
            className="absolute -right-3 top-6 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-sidebar text-muted-foreground transition-colors hover:text-foreground"
          >
            <PanelLeftClose className="h-3.5 w-3.5" />
          </button>
        )}

        {!mobile && collapsed && (
          <button
            onClick={onToggle}
            data-testid="sidebar-expand-btn"
            className="mx-auto mt-2 flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
          >
            <PanelLeft className="h-4 w-4" />
          </button>
        )}

        {/* Nav */}
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              {!collapsed && (
                <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{group.label}</p>
              )}
              {collapsed && <div className="mx-3 mb-2 h-px bg-line" />}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavItem key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer user */}
        <div className="shrink-0 border-t border-line p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg p-1.5 text-left transition-colors hover:bg-white/[0.04]",
                  collapsed && "justify-center",
                )}
              >
                <UserAvatar initials={initialsOf(displayName)} name={displayName} />
                {!collapsed && (
                  <>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">{displayName}</p>
                      <p className="truncate text-xs capitalize text-muted-foreground">{displayRole}</p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56 border-line bg-popover">
              <DropdownMenuLabel>
                <p className="text-sm font-semibold text-foreground">{displayName}</p>
                <p className="text-xs font-normal text-muted-foreground">{displayEmail}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-line" />
              <DropdownMenuItem onClick={() => demoAction("Perfil")} data-testid="user-menu-profile"><User className="mr-2 h-4 w-4" />Meu perfil</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/configuracoes")} data-testid="user-menu-settings"><Settings className="mr-2 h-4 w-4" />Configurações</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/portal")} data-testid="user-menu-portal"><LifeBuoy className="mr-2 h-4 w-4" />Portal do Cliente</DropdownMenuItem>
              <DropdownMenuSeparator className="bg-line" />
              <DropdownMenuItem onClick={handleLogout} data-testid="user-menu-logout" className="text-err focus:text-err"><LogOut className="mr-2 h-4 w-4" />Sair</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    </TooltipProvider>
  );
};
